import React, { useEffect, useState, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BarChart3,
  Loader2,
  Filter,
  Download,
  X,
  Search,
  MapPin,
  Target,
  FileText,
  Calendar,
  Activity,
  DollarSign,
  ChevronDown,
  Check,
} from 'lucide-react';

const STATUS_STYLES = {
  completed: 'bg-green-100 text-green-800 border-green-200',
  'in-progress': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'on-hold': 'bg-orange-100 text-orange-800 border-orange-200',
  'submitted to sda': 'bg-slate-100 text-slate-800 border-slate-200',
  'approved by sda': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  "awaiting stakeholder's response": 'bg-amber-100 text-amber-800 border-amber-200',
  'awaiting stakeholders response': 'bg-amber-100 text-amber-800 border-amber-200',
  'awaiting bee response': 'bg-sky-100 text-sky-800 border-sky-200',
};

const getStatusClassName = (status) => {
  if (!status || typeof status !== 'string') return 'bg-gray-100 text-gray-800 border-gray-200';
  const key = status.trim().toLowerCase();
  return STATUS_STYLES[key] ?? 'bg-gray-100 text-gray-800 border-gray-200';
};

const PAYMENT_STATUS_STYLES = {
  released: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'pending at sda': 'bg-violet-100 text-violet-800 border-violet-200',
  'awaiting funds from bee': 'bg-amber-100 text-amber-800 border-amber-200',
};

const getPaymentStatusClassName = (paymentStatus) => {
  if (!paymentStatus || typeof paymentStatus !== 'string') return 'bg-gray-100 text-gray-800 border-gray-200';
  const key = paymentStatus.trim().toLowerCase();
  return PAYMENT_STATUS_STYLES[key] ?? 'bg-gray-100 text-gray-800 border-gray-200';
};

const formatSubmissionDate = (value) => {
  if (value == null) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatPaymentReleasedDate = (value) => {
  if (value == null) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const DgDashboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdowns, setDropdowns] = useState({ cells: [], tasks: [], subtasks: [], quarters: [], statuses: [] });
  const [paymentStatusMaster, setPaymentStatusMaster] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(true);
  const [filters, setFilters] = useState({
    cellId: '',
    taskId: '',
    subtaskId: '',
    quarterId: '',
    statusId: '',
    paymentStatusId: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'taskId') setFilters((prev) => ({ ...prev, subtaskId: '' }));
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '') || searchTerm !== '';

  const clearFilters = () => {
    setFilters({
      cellId: '',
      taskId: '',
      subtaskId: '',
      quarterId: '',
      statusId: '',
      paymentStatusId: '',
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchDropdowns = async () => {
      setDropdownsLoading(true);
      try {
        const [ddRes, psRes] = await Promise.all([
          axios.get('/api/masters/dropdowns'),
          axios.get('/api/masters/paymentstatusmaster'),
        ]);
        const data = ddRes.data || {};
        setDropdowns({
          cells: data.cells || [],
          tasks: data.tasks || [],
          subtasks: data.subtasks || [],
          quarters: data.quarters || [],
          statuses: data.statuses || [],
        });
        setPaymentStatusMaster(Array.isArray(psRes.data) ? psRes.data : []);
      } catch (e) {
        console.error('DG dropdowns fetch error:', e);
        toast.error('Failed to load filter options.');
      } finally {
        setDropdownsLoading(false);
      }
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.cellId) params.cellId = filters.cellId;
        if (filters.taskId) params.taskId = filters.taskId;
        if (filters.subtaskId) params.subtaskId = filters.subtaskId;
        if (filters.quarterId) params.quarterId = filters.quarterId;
        if (filters.statusId) params.statusId = filters.statusId;
        if (filters.paymentStatusId) params.paymentStatusId = filters.paymentStatusId;
        const { data } = await axios.get('/api/dashboard/dg', { params });
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('DG dashboard fetch error:', e);
        toast.error('Failed to load DG dashboard data.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.cellId, filters.taskId, filters.subtaskId, filters.quarterId, filters.statusId, filters.paymentStatusId]);

  const filteredBySearch = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        (row.cell_name != null && String(row.cell_name).toLowerCase().includes(lower)) ||
        (row.state_name != null && String(row.state_name).toLowerCase().includes(lower)) ||
        (row.task_description != null && String(row.task_description).toLowerCase().includes(lower)) ||
        (row.subtask_description != null && String(row.subtask_description).toLowerCase().includes(lower)) ||
        (row.quarter != null && String(row.quarter).toLowerCase().includes(lower)) ||
        (row.payment_status != null && String(row.payment_status).toLowerCase().includes(lower)) ||
        (row.status != null && String(row.status).toLowerCase().includes(lower))
    );
  }, [rows, searchTerm]);

  // Paginate by cell: one page = all rows for one cell
  const { cellGroups, totalPages, paginatedRows, currentCellLabel } = useMemo(() => {
    const byCell = new Map();
    filteredBySearch.forEach((row) => {
      const key = row.cell_id;
      if (!byCell.has(key)) byCell.set(key, []);
      byCell.get(key).push(row);
    });
    const cells = Array.from(byCell.entries())
      .map(([cellId, rows]) => ({
        cellId,
        cellLabel: rows[0] ? `${rows[0].state_name || rows[0].cell_name || 'Cell'} - Cell ${rows[0].cell_name ?? cellId}` : `Cell ${cellId}`,
        rows,
      }))
      .sort((a, b) => {
        const aState = (a.rows[0]?.state_name || '').toLowerCase();
        const bState = (b.rows[0]?.state_name || '').toLowerCase();
        if (aState !== bState) return aState.localeCompare(bState);
        const aCell = String(a.rows[0]?.cell_name ?? a.cellId).toLowerCase();
        const bCell = String(b.rows[0]?.cell_name ?? b.cellId).toLowerCase();
        return aCell.localeCompare(bCell);
      });
    const total = Math.max(1, cells.length);
    const pageIndex = Math.min(Math.max(1, currentPage), total) - 1;
    const current = cells[pageIndex];
    return {
      cellGroups: cells,
      totalPages: total,
      paginatedRows: current ? current.rows : [],
      currentCellLabel: current ? current.cellLabel : '',
    };
  }, [filteredBySearch, currentPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const rowSpans = useMemo(() => {
    const list = paginatedRows;
    const cellSpan = [];
    const quarterSpan = [];
    const taskSpan = [];
    for (let i = 0; i < list.length; i++) {
      let c = 1;
      while (i + c < list.length && list[i + c].cell_id === list[i].cell_id) c++;
      cellSpan[i] = c;
      let q = 1;
      while (
        i + q < list.length &&
        list[i + q].cell_id === list[i].cell_id &&
        list[i + q].quarter_id === list[i].quarter_id
      )
        q++;
      quarterSpan[i] = q;
      let t = 1;
      while (
        i + t < list.length &&
        list[i + t].cell_id === list[i].cell_id &&
        list[i + t].task_id === list[i].task_id &&
        list[i + t].quarter_id === list[i].quarter_id
      )
        t++;
      taskSpan[i] = t;
    }
    return { cellSpan, quarterSpan, taskSpan };
  }, [paginatedRows]);

  const exportToExcel = () => {
    if (filteredBySearch.length === 0) return;
    const headers = ['State', 'Cell name', 'Task', 'Subtask', 'Quarter', 'Status', 'Payment status'];
    const csvRows = [
      headers.join(','),
      ...filteredBySearch.map((row) =>
        [
          `"${String(row.state_name || '').replace(/"/g, '""')}"`,
          `"${String(row.cell_name ?? '').replace(/"/g, '""')}"`,
          `"${String(row.task_no != null ? `Task ${row.task_no}: ${row.task_description || ''}` : '').replace(/"/g, '""')}"`,
          `"${String(row.subtask_no != null ? `Subtask ${row.subtask_no}: ${row.subtask_description || ''}` : '').replace(/"/g, '""')}"`,
          `"${String(row.quarter ?? '').replace(/"/g, '""')}"`,
          `"${String(row.status ?? '').replace(/"/g, '""')}"`,
          `"${String(row.payment_status ?? '').replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `dg-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const subtaskSortKey = (no) => {
    if (no == null) return [0, ''];
    const s = String(no).trim();
    const numMatch = s.match(/^\d+/);
    const num = numMatch ? parseInt(numMatch[0], 10) : 0;
    const rest = numMatch ? s.slice(numMatch[0].length) : s;
    return [num, rest];
  };

  const filteredSubtasks = useMemo(() => {
    const list = !filters.taskId
      ? dropdowns.subtasks || []
      : (dropdowns.subtasks || []).filter(
          (s) => String(s.Task_ID || s.task_id) === String(filters.taskId)
        );
    return [...list].sort((a, b) => {
      const taskA = a.Task_ID ?? a.task_id ?? 0;
      const taskB = b.Task_ID ?? b.task_id ?? 0;
      if (taskA !== taskB) return taskA - taskB;
      const [na, ra] = subtaskSortKey(a.Subtask_No ?? a.subtask_no);
      const [nb, rb] = subtaskSortKey(b.Subtask_No ?? b.subtask_no);
      if (na !== nb) return na - nb;
      return String(ra).localeCompare(String(rb), undefined, { sensitivity: 'base' });
    });
  }, [dropdowns.subtasks, filters.taskId]);

  const quarters = dropdowns.quarters || [];
  const statuses = dropdowns.statuses || [];
  const cells = dropdowns.cells || [];
  const tasks = dropdowns.tasks || [];

  return (
    <div className="p-6">
      <div className="bg-blue-600 rounded-xl p-6 shadow-sm mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-white/20 rounded-lg">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">DG dashboard</h1>
        </div>
        <p className="text-blue-100 text-sm ml-11">Director General dashboard view</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Filter className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Task Details & Filters
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:shadow-md border border-gray-200"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </button>
              )}
              <button
                onClick={exportToExcel}
                disabled={!filteredBySearch.length}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg transform hover:scale-105 disabled:hover:scale-100"
              >
                <Download className="h-4 w-4" />
                Export Excel
              </button>
            </div>
          </div>

          <div className="mb-6">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
              <input
                type="text"
                placeholder="Search by cell name, state, quarter, status, or payment status..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 items-start">
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Quarter
              </label>
              <select
                value={filters.quarterId}
                onChange={(e) => handleFilterChange('quarterId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium"
              >
                <option value="">All Quarters</option>
                {quarters.map((q) => (
                  <option key={q.ID ?? q.id} value={q.ID ?? q.id}>
                    {q.Quarter_Name ?? q.quarter_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                Cell
              </label>
              <select
                value={filters.cellId}
                onChange={(e) => handleFilterChange('cellId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium"
              >
                <option value="">All Cells</option>
                {(cells || []).map((cell) => (
                  <option key={cell.Cell_ID || cell.cell_id} value={cell.Cell_ID ?? cell.cell_id}>
                    {(cell.State_UT || cell.state_ut || 'State')} - Cell {cell.Cell_No ?? cell.cell_no}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Target className="h-3.5 w-3.5" />
                Task
              </label>
              <Listbox
                value={filters.taskId}
                onChange={(value) => {
                  handleFilterChange('taskId', value);
                  setFilters((prev) => ({ ...prev, subtaskId: '' }));
                }}
              >
                <div className="relative">
                  <Listbox.Button className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium text-left flex items-center justify-between">
                    <span className="block truncate flex-1 min-w-0 pr-2">
                      {filters.taskId
                        ? (() => {
                            const task = (tasks || []).find((t) => String(t.Task_ID ?? t.task_id) === String(filters.taskId));
                            if (!task) return 'All Tasks';
                            return `Task ${task.Task_No ?? task.task_no}: ${task.Task_Description || task.task_description || ''}`;
                          })()
                        : 'All Tasks'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  </Listbox.Button>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-[9999] mt-1 w-full min-w-[320px] max-w-[480px] max-h-72 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200">
                      <Listbox.Option
                        value=""
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>All Tasks</span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                <Check className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                      {(tasks || []).map((task) => {
                        const text = `Task ${task.Task_No ?? task.task_no}: ${task.Task_Description || task.task_description || ''}`;
                        return (
                          <Listbox.Option
                            key={task.Task_ID ?? task.task_id}
                            value={String(task.Task_ID ?? task.task_id)}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block break-words whitespace-normal ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {text}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                Subtask
              </label>
              <Listbox value={filters.subtaskId} onChange={(value) => handleFilterChange('subtaskId', value)}>
                <div className="relative">
                  <Listbox.Button className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium text-left flex items-center justify-between">
                    <span className="block truncate flex-1 min-w-0 pr-2">
                      {filters.subtaskId
                        ? (() => {
                            const subtask = filteredSubtasks.find(
                              (s) => String(s.Subtask_ID ?? s.subtask_id) === String(filters.subtaskId)
                            );
                            if (!subtask) return 'All Subtasks';
                            const taskNo = (tasks || []).find((t) => (t.Task_ID ?? t.task_id) === (subtask.Task_ID ?? subtask.task_id))?.Task_No ?? subtask.Task_ID ?? subtask.task_id;
                            return `Task ${taskNo} - Subtask ${subtask.Subtask_No ?? subtask.subtask_no}: ${subtask.Subtask_Description || subtask.subtask_description || ''}`;
                          })()
                        : 'All Subtasks'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  </Listbox.Button>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-[9999] mt-1 w-full min-w-[320px] max-w-[480px] max-h-72 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200">
                      <Listbox.Option
                        value=""
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>All Subtasks</span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                <Check className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                      {filteredSubtasks.map((subtask) => {
                        const taskNo = (tasks || []).find((t) => (t.Task_ID ?? t.task_id) === (subtask.Task_ID ?? subtask.task_id))?.Task_No ?? subtask.Task_ID ?? subtask.task_id;
                        const text = `Task ${taskNo} - Subtask ${subtask.Subtask_No ?? subtask.subtask_no}: ${subtask.Subtask_Description || subtask.subtask_description || ''}`;
                        return (
                          <Listbox.Option
                            key={subtask.Subtask_ID ?? subtask.subtask_id}
                            value={String(subtask.Subtask_ID ?? subtask.subtask_id)}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block break-words whitespace-normal ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {text}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Activity className="h-3.5 w-3.5" />
                Status
              </label>
              <select
                value={filters.statusId}
                onChange={(e) => handleFilterChange('statusId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium"
              >
                <option value="">All Status</option>
                {statuses.map((s) => (
                  <option key={s.Status_ID ?? s.status_id} value={s.Status_ID ?? s.status_id}>
                    {s.Status ?? s.status}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" />
                Payment status
              </label>
              <select
                value={filters.paymentStatusId}
                onChange={(e) => handleFilterChange('paymentStatusId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md text-sm font-medium"
              >
                <option value="">All Payment Status</option>
                {paymentStatusMaster.map((ps) => (
                  <option key={ps.id} value={ps.id}>
                    {ps.status ?? ps.payment_status ?? `ID ${ps.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredBySearch.length > 0 && (
          <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-blue-50 border-b-2 border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs font-semibold text-purple-600 bg-purple-100 px-3 py-1 rounded-lg">
              {currentCellLabel ? `${currentCellLabel} (${paginatedRows.length} entries)` : `${filteredBySearch.length} entries`}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-md">
                  Cell {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading || dropdownsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredBySearch.length === 0 ? (
            <div className="text-center py-16">
              <Search className="h-10 w-10 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No data found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters or search criteria</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  <X className="h-4 w-4" />
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <table className="w-full min-w-[800px]" style={{ borderCollapse: 'collapse' }}>
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap w-0 max-w-[11rem]" style={{ width: '11rem' }}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600 shrink-0" />
                      Cell name
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-orange-600" />
                      Quarter
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-amber-600" />
                      Payment status
                    </div>
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider max-w-[14rem]" style={{ width: '14rem' }}>
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-600 shrink-0" />
                      Task
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      Sub task
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-red-600" />
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedRows.map((row, i) => {
                  const isFirstInCell = i === 0 || paginatedRows[i - 1].cell_id !== row.cell_id;
                  const isFirstInQuarter =
                    i === 0 ||
                    paginatedRows[i - 1].cell_id !== row.cell_id ||
                    paginatedRows[i - 1].quarter_id !== row.quarter_id;
                  const isFirstInTask =
                    i === 0 ||
                    paginatedRows[i - 1].cell_id !== row.cell_id ||
                    paginatedRows[i - 1].task_id !== row.task_id ||
                    paginatedRows[i - 1].quarter_id !== row.quarter_id;
                  const rowKey = `${row.cell_id}-${row.task_id}-${row.subtask_id}-${row.quarter_id}-${i}`;
                  return (
                    <tr
                      key={rowKey}
                      className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                    >
                      {isFirstInCell && (
                        <td
                          rowSpan={rowSpans.cellSpan[i]}
                          className="px-4 py-5 align-top border-r border-gray-200 max-w-0"
                          style={{ verticalAlign: 'top', width: '11rem', maxWidth: '11rem' }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                              <MapPin className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-bold text-gray-900 text-sm break-words leading-snug">{row.state_name || row.cell_name || '—'}</div>
                              <div className="text-xs text-gray-500 font-medium mt-0.5">Cell {row.cell_name ?? '—'}</div>
                            </div>
                          </div>
                        </td>
                      )}
                      {isFirstInQuarter && (
                        <td
                          rowSpan={rowSpans.quarterSpan[i]}
                          className="px-6 py-5 whitespace-nowrap align-top border-r border-gray-200"
                          style={{ verticalAlign: 'top' }}
                        >
                          <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-200">
                            {row.quarter ?? '—'}
                          </span>
                        </td>
                      )}
                      {isFirstInQuarter && (
                        <td
                          rowSpan={rowSpans.quarterSpan[i]}
                          className="px-6 py-5 text-sm align-top border-r border-gray-200"
                          style={{ verticalAlign: 'top' }}
                        >
                          {row.payment_status || row.payment_submission_date || row.payment_received_date ? (
                            <div className="flex flex-col gap-0.5 items-center">
                              {row.payment_status && (
                                <span className={`inline-block text-xs font-semibold px-2 py-1 rounded border w-fit ${getPaymentStatusClassName(row.payment_status)}`}>
                                  {row.payment_status}
                                </span>
                              )}
                              {row.payment_submission_date && (
                                <span className="text-[11px] text-gray-500 text-center">
                                  ({formatSubmissionDate(row.payment_submission_date)})
                                </span>
                              )}
                              {row.payment_status && String(row.payment_status).trim().toLowerCase() === 'released' && row.payment_received_date && (
                                <span className="inline-block text-[11px] font-medium text-emerald-800 text-center mt-0.5 px-2 py-1 rounded bg-emerald-50 border border-emerald-200">
                                  Payment Released date - {formatPaymentReleasedDate(row.payment_received_date)}
                                </span>
                              )}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      {isFirstInTask && (
                        <td
                          rowSpan={rowSpans.taskSpan[i]}
                          className="px-4 py-5 align-top border-r border-gray-200 max-w-0"
                          style={{ verticalAlign: 'top', width: '14rem', maxWidth: '14rem' }}
                        >
                          <div className="min-w-0">
                            {row.task_no != null && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200 shrink-0">
                                  Task {row.task_no}
                                </span>
                              </div>
                            )}
                            <div className="text-sm font-medium text-gray-900 break-words" title={row.task_description || ''}>
                              {row.task_description || '—'}
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-5 border-r border-gray-200" style={{ verticalAlign: 'top' }}>
                        <div className="max-w-md">
                          {row.subtask_no != null && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                                Subtask {row.subtask_no}
                              </span>
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                            {row.subtask_description || '—'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-sm" style={{ verticalAlign: 'top' }}>
                        {row.status || row.status_submission_date ? (
                          <div className="flex flex-col gap-0.5 items-center">
                            {row.status && (
                              <span className={`inline-block text-xs font-semibold px-2 py-1 rounded border w-fit ${getStatusClassName(row.status)}`}>
                                {row.status}
                              </span>
                            )}
                            {row.status_submission_date && (
                              <span className="text-[11px] text-gray-500 text-center">
                                ({formatSubmissionDate(row.status_submission_date)})
                              </span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DgDashboard;
